import { RefObject, useEffect } from 'react';
import { AllowChangeGrid } from '../../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { AddOrderProductTestIds } from '../../../../../../utils/testIds/OrderProducts.testIds';
import { validateTouchEnabled } from '../../../../../../utils/validators';
import { OfflineStatus } from '../../../../../offline/domain/offlineOrder/offlineOrder.types';
import { AvailabilityEnum, ESProductColor } from '../../../../../products/ProductTypes';
import { OrderStatus } from '../../../../interfaces/order-status.interface';
import { AddOrderProductMode } from '../../../orderProducts.types';
import { OrderProductForGridContainer } from '../../../product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { useAddOrderProduct } from '../../useAddOrderProduct';
import { AddOrderProductColorOption } from '../addOrderProductRow.types';

const ADD_PRODUCT_GRID_INPUT_SELECTOR = `[test-id="${AddOrderProductTestIds.PRODUCT_GRID}"] input[type="number"]:not(:disabled)`;
const ADD_PRODUCT_DELIVERY_WINDOW_SELECTOR = `[test-id="${AddOrderProductTestIds.DELIVERY_WINDOW}"]`;
const ADD_PRODUCT_COLOR_SELECTOR = `[test-id="${AddOrderProductTestIds.COLOR_AUTOCOMPLETE}"]`;

const canTakeFocusFrom = (codeInputRef: RefObject<HTMLInputElement | null>) => {
  const focusedElement = document.activeElement;

  if (!focusedElement || focusedElement === document.body) {
    return true;
  }

  return (
    focusedElement === codeInputRef.current ||
    !!focusedElement.closest(ADD_PRODUCT_COLOR_SELECTOR) ||
    !!focusedElement.closest(ADD_PRODUCT_DELIVERY_WINDOW_SELECTOR)
  );
};

const toColorOption = (color: ESProductColor): AddOrderProductColorOption => ({
  id: color.id,
  code: color.code,
  name: color.name,
  isImage: color.isImage,
  hexcode: color.hexcode,
  imageUrl: color.imageUrl,
});

interface UseAddOrderProductRowParams {
  orderId: string;
  orderStatus: OrderStatus | OfflineStatus;
  mode?: AddOrderProductMode;
}

export const useAddOrderProductRow = ({
  orderId,
  orderStatus,
  mode,
}: UseAddOrderProductRowParams) => {
  const addOrderProduct = useAddOrderProduct({ orderId, orderStatus, mode });
  const { product, color, colors, availability, deliveryWindow, handleSelectColor } =
    addOrderProduct;

  const hasGrid = addOrderProduct.grid.length > 0;
  const isScanningBySku = mode === AddOrderProductMode.SKU;
  const isResolvingDeliveryWindow = addOrderProduct.isResolvingDeliveryWindow;
  const needsColor = !!product && !color && colors.length > 0;
  const needsDeliveryWindow = !!color && !deliveryWindow && availability === AvailabilityEnum.EMPTY;

  useEffect(() => {
    if (!hasGrid || isScanningBySku || validateTouchEnabled()) {
      return;
    }

    if (!canTakeFocusFrom(addOrderProduct.codeInputRef)) {
      return;
    }

    const firstGridInput = document.querySelector<HTMLInputElement>(
      ADD_PRODUCT_GRID_INPUT_SELECTOR,
    );

    firstGridInput?.focus();
    firstGridInput?.select();
  }, [hasGrid, isScanningBySku, addOrderProduct.codeInputRef]);

  useEffect(() => {
    if (!needsColor || isScanningBySku || validateTouchEnabled()) {
      return;
    }

    if (!canTakeFocusFrom(addOrderProduct.codeInputRef)) {
      return;
    }

    document.querySelector<HTMLElement>(ADD_PRODUCT_COLOR_SELECTOR)?.focus();
  }, [needsColor, isScanningBySku, addOrderProduct.codeInputRef]);

  useEffect(() => {
    if (hasGrid || !needsDeliveryWindow || isResolvingDeliveryWindow) {
      return;
    }

    if (isScanningBySku || validateTouchEnabled()) {
      return;
    }

    if (!canTakeFocusFrom(addOrderProduct.codeInputRef)) {
      return;
    }

    document.querySelector<HTMLElement>(ADD_PRODUCT_DELIVERY_WINDOW_SELECTOR)?.focus();
  }, [
    hasGrid,
    needsDeliveryWindow,
    isResolvingDeliveryWindow,
    isScanningBySku,
    addOrderProduct.codeInputRef,
  ]);

  const isPreparingProduct =
    addOrderProduct.isLoadingProduct || addOrderProduct.isPreparingSearchedProduct;

  const colorOptions = colors.map(toColorOption);
  const selectedColorOption = color ? toColorOption(color) : null;

  const onSelectColorOption = (option: AddOrderProductColorOption) => {
    const selectedColor = colors.find((current) => current.id === option.id);

    if (selectedColor) {
      handleSelectColor(selectedColor);
    }
  };

  const gridProduct: OrderProductForGridContainer = {
    productId: product?.id || '',
    productColorId: color?.id || '',
    availability,
    deliveryWindowId: deliveryWindow?.id,
    productAllowChangeImmediateGrid: color?.allowChangeImmediateGrid || AllowChangeGrid.ALLOW,
    productAllowChangePreOrderGrid: color?.allowChangePreOrderGrid || AllowChangeGrid.ALLOW,
  };

  return {
    ...addOrderProduct,
    isPreparingProduct,
    gridProduct,
    colorOptions,
    selectedColorOption,
    onSelectColorOption,
  };
};
